/** 시수 자동 조정: 편제별 1·2학기 구분/통합 + 자동 조정 미포함 + 학습계획 시수 */

export type TrackHoursTermScope = 'split' | 'unified';

export type TrackHoursAdjustTrackSettings = {
  termScope: TrackHoursTermScope;
  /** true면 이후 자동 조정 실행 시 해당 편제 제외 */
  excludeFromAdjust: boolean;
  /** 구분 편제: 1학기 학습계획 시수 (null=미입력) */
  learningTerm1: number | null;
  /** 구분 편제: 2학기 학습계획 시수 */
  learningTerm2: number | null;
  /** 통합 편제: 연간 학습계획 시수 */
  learningAnnual: number | null;
};

export const TRACK_HOURS_TERM_SCOPE_CHANGE_EVENT = 'cp:track-hours-term-scope-change';

function v1Key(workspaceId: string, gradeId: string): string {
  return `cp.trackHoursTermScope.v1.${workspaceId}.${gradeId}`;
}

function v2Key(workspaceId: string, gradeId: string): string {
  return `cp.trackHoursAdjustSettings.v2.${workspaceId}.${gradeId}`;
}

function isScope(v: unknown): v is TrackHoursTermScope {
  return v === 'split' || v === 'unified';
}

function parseLearningHours(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.min(9999, Math.max(0, Math.round(n)));
}

export function emptyTrackHoursAdjustSettings(
  overrides?: Partial<TrackHoursAdjustTrackSettings>
): TrackHoursAdjustTrackSettings {
  return {
    termScope: 'split',
    excludeFromAdjust: false,
    learningTerm1: null,
    learningTerm2: null,
    learningAnnual: null,
    ...overrides,
  };
}

function normalizeSettings(raw: unknown): TrackHoursAdjustTrackSettings | null {
  if (isScope(raw)) {
    return emptyTrackHoursAdjustSettings({ termScope: raw });
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const obj = raw as Record<string, unknown>;
  const termScope = isScope(obj.termScope) ? obj.termScope : 'split';
  return emptyTrackHoursAdjustSettings({
    termScope,
    excludeFromAdjust: obj.excludeFromAdjust === true,
    learningTerm1: parseLearningHours(obj.learningTerm1),
    learningTerm2: parseLearningHours(obj.learningTerm2),
    learningAnnual: parseLearningHours(obj.learningAnnual),
  });
}

/** 저장된 편제 설정 → 모달 입력 키 (`u:편제` / `1:편제` / `2:편제`) */
export function hoursAdjustSettingsToLearningKeyMap(
  settings: Record<string, TrackHoursAdjustTrackSettings>
): Record<string, string> {
  const out: Record<string, string> = {};
  Object.entries(settings).forEach(([trackName, s]) => {
    const name = trackName.trim();
    if (!name) return;
    if (s.termScope === 'unified') {
      if (s.learningAnnual != null) out[`u:${name}`] = String(s.learningAnnual);
    } else {
      if (s.learningTerm1 != null) out[`1:${name}`] = String(s.learningTerm1);
      if (s.learningTerm2 != null) out[`2:${name}`] = String(s.learningTerm2);
    }
  });
  return out;
}

export function loadTrackHoursAdjustSettingsMap(
  workspaceId: string,
  gradeId: string
): Record<string, TrackHoursAdjustTrackSettings> {
  if (!workspaceId || !gradeId) return {};
  try {
    const v2raw = window.localStorage.getItem(v2Key(workspaceId, gradeId));
    if (v2raw) {
      const parsed = JSON.parse(v2raw) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const out: Record<string, TrackHoursAdjustTrackSettings> = {};
        Object.entries(parsed as Record<string, unknown>).forEach(([trackName, val]) => {
          if (!trackName.trim()) return;
          const normalized = normalizeSettings(val);
          if (normalized) out[trackName] = normalized;
        });
        return out;
      }
    }

    // v1: trackName → 'split' | 'unified'
    const v1raw = window.localStorage.getItem(v1Key(workspaceId, gradeId));
    if (!v1raw) return {};
    const parsed = JSON.parse(v1raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, TrackHoursAdjustTrackSettings> = {};
    Object.entries(parsed as Record<string, unknown>).forEach(([trackName, val]) => {
      if (!trackName.trim() || !isScope(val)) return;
      out[trackName] = emptyTrackHoursAdjustSettings({ termScope: val });
    });
    return out;
  } catch {
    return {};
  }
}

export function saveTrackHoursAdjustSettingsMap(
  workspaceId: string,
  gradeId: string,
  value: Record<string, TrackHoursAdjustTrackSettings>
): void {
  if (!workspaceId || !gradeId) return;
  try {
    window.localStorage.setItem(v2Key(workspaceId, gradeId), JSON.stringify(value));
    window.dispatchEvent(new CustomEvent(TRACK_HOURS_TERM_SCOPE_CHANGE_EVENT));
  } catch {
    /* ignore quota */
  }
}

/** @deprecated loadTrackHoursAdjustSettingsMap 사용 권장 — 하위 호환 */
export function loadTrackHoursTermScopeMap(
  workspaceId: string,
  gradeId: string
): Record<string, TrackHoursTermScope> {
  const settings = loadTrackHoursAdjustSettingsMap(workspaceId, gradeId);
  const out: Record<string, TrackHoursTermScope> = {};
  Object.entries(settings).forEach(([trackName, s]) => {
    out[trackName] = s.termScope;
  });
  return out;
}

/** @deprecated saveTrackHoursAdjustSettingsMap 사용 권장 — 하위 호환 */
export function saveTrackHoursTermScopeMap(
  workspaceId: string,
  gradeId: string,
  value: Record<string, TrackHoursTermScope>
): void {
  const prev = loadTrackHoursAdjustSettingsMap(workspaceId, gradeId);
  const next: Record<string, TrackHoursAdjustTrackSettings> = { ...prev };
  Object.entries(value).forEach(([trackName, termScope]) => {
    const cur = prev[trackName] ?? emptyTrackHoursAdjustSettings();
    next[trackName] = {
      ...cur,
      termScope,
    };
  });
  saveTrackHoursAdjustSettingsMap(workspaceId, gradeId, next);
}

export function getTrackHoursAdjustSettings(
  map: Record<string, TrackHoursAdjustTrackSettings>,
  trackName: string
): TrackHoursAdjustTrackSettings {
  return map[trackName] ?? emptyTrackHoursAdjustSettings();
}

export function trackHoursTermScopeLabel(scope: TrackHoursTermScope): string {
  return scope === 'unified' ? '1·2학기 통합' : '1·2학기 구분';
}
